import { useState, useEffect } from 'react'
import sortBy from 'lodash/sortBy'
import moment, { MomentInput } from 'moment'
import {
  FIELDS,
  EVAL_FORM_STATUS,
} from './efmConstants'
import { TFunction } from 'react-i18next'
import { TFunctionResult } from 'i18next'
import { OptionState, TransformedAnswerGroupData } from './efmTypes'
import { cloneDeep, isEmpty, isEqual, xorWith } from 'lodash'

export const getDisplayValue = (
  field: string,
  value: MomentInput,
  t: TFunction,
): TFunctionResult | MomentInput => {
  switch (field) {
    case FIELDS.NAME:
      return value
    case FIELDS.STATUS: {
      if (value === EVAL_FORM_STATUS.DRAFT) {
        return t('editable')
      } else if (value === EVAL_FORM_STATUS.PUBLISHED) {
        return t('published')
      } else if (value === EVAL_FORM_STATUS.ARCHIVED) {
        return t('archived')
      } else {
        return t('unknown')
      }
    }
    case FIELDS.CREATED:
    case FIELDS.UPDATED:
      return moment(value).format('L')
    default:
      return value
  }
}

export const sortData = (
  data: $TSFIXME,
  sortByField: string,
  sortOrder: string,
  t: TFunction,
): $TSFIXME => {
  const getSortableValue = (row: { [x: string]: $TSFIXME, status: number }) => {
    switch (sortByField) {
      case FIELDS.STATUS:
        return getDisplayValue(sortByField, row.status, t)
      default:
        return row[sortByField]
    }
  }

  const sortedData = sortByField === FIELDS.NAME ? sortBy(data, [row => getSortableValue(row).toLowerCase()]) : sortBy(data, [row => getSortableValue(row)])
  if (sortOrder === 'desc') {
    sortedData.reverse()
  }

  return sortedData
}

export const useDebounce = (
  value: number | string,
  buffer: number,
): number | string => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value)
    }, buffer)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [value, buffer])
  return debouncedValue
}

export const getSectionNameField = (section: { id: number }): string =>
  `${section.id}-name`

export const arrayMove = (
  arr: $TSFIXME,
  fromIndex: number,
  toIndex: number,
): $TSFIXME => {
  const copy = cloneDeep(arr);
  [copy[fromIndex], copy[toIndex]] = [copy[toIndex], copy[fromIndex]]
  return copy
}

/**
 * Returns true if string is unique. Would like to abstract to use with a 'key' arg but keeping it fixed on answer group 'name' for now. All values enter trimmed.
 *
 * @param {Array} collection - Array containg strings for comparison
 * @param {string} value - the string you are evaluating
 */
export const isStringUnique = (
  // how do you generically type an array of objects so that this can be used reused without implying any...
  collection: TransformedAnswerGroupData[],
  value: string,
): boolean => {
  const stringList = []
  for (const entry of collection) {
    stringList.push(entry.name.toLowerCase())
  }
  const isUnique = !stringList.includes(value.toLowerCase())
  return isUnique
}

const ArrayEqualLodash = (array1: {label: string, points: number}[], array2: {label: string, points: number}[]) => {
  return  isEmpty(xorWith(array1, array2, isEqual))
}

export const isOptionArrayUnique = (
  initialArray: TransformedAnswerGroupData[],
  currentArray: OptionState[],
): {success: boolean, reference: string | undefined} => {
  const results = []
  const allLabelsAndPoints = []
  const currentLabelsAndPoints = currentArray.map((option: { label: string, points: number }) => {
    return { label: option.label.trim().toLowerCase(), points: option.points }
  })
  // grab only labels and points for comparison
  for (const template of initialArray) {
    const existingOptions = template.options.map(
      (entry: { label: string, points: number }) => {
        return { label: entry.label.toLowerCase(), points: entry.points }
      },
    )
    allLabelsAndPoints.push(existingOptions)
  }
  // run arrayEquals on each existing answer group
  for (const array of allLabelsAndPoints) {
    const lengthCheck = array.length === currentLabelsAndPoints.length
    const res = lengthCheck ? ArrayEqualLodash(array, currentLabelsAndPoints) : false
    results.push(res)
  }
  // if our results array includes a `true` then our option array is not unique => return false
  const result = !results.includes(true)
  const targetIndex = results.findIndex(x => x === true)
  // find the answer group name that already has this configuration if applicable
  const refName = result ? undefined : initialArray[targetIndex]?.name
  return { success: result, reference: refName }
}
